import React, { useState } from "react";
import {
  useCreateTournament,
  useDeleteTournament,
  useTournaments,
  useUpdateTournament,
  useUploadTournamentPhoto,
} from "../apiCalls/tournamentApiCalls";
import {
  Button,
  Card,
  Cell,
  EmptyState,
  ErrorState,
  Field,
  Input,
  Loader,
  PageHeader,
  Row,
  Table,
  Textarea,
} from "../components/ui";
import Modal from "../components/Modal";
import ConfirmDelete from "../components/ConfirmDelete";
import { apiDateToInput, formatDate, inputDateToApi } from "../utils";

const HEAD = [
  { label: "", key: "photo" },
  { label: "Tournament" },
  { label: "Date" },
  { label: "Location" },
  { label: "", key: "actions", align: "right" },
];

// 5 MB, matching what the API's sharp pipeline will happily chew through.
const MAX_PHOTO_BYTES = 5 * 1024 * 1024;

// The tournament hero image. The upload endpoint needs an id, so on a brand
// new tournament the file is held here and sent straight after the create
// succeeds — the admin picks the photo once, in one form.
function PhotoField({ currentUrl, file, onPick, onClear }) {
  const [error, setError] = useState("");

  const handleChange = (event) => {
    const picked = event.target.files?.[0];
    if (!picked) return;

    if (!picked.type.startsWith("image/")) {
      setError("That file isn't an image.");
      return;
    }
    if (picked.size > MAX_PHOTO_BYTES) {
      setError("Images must be 5 MB or smaller.");
      return;
    }

    setError("");
    onPick(picked);
  };

  // Object URLs are revoked on unmount by the browser when the page navigates,
  // but a long-lived modal can churn through several — release the old one as
  // soon as a new file replaces it.
  const preview = file ? URL.createObjectURL(file) : currentUrl;

  return (
    <Field
      label="Photo"
      hint="16:9 works best. Shown on the tournament card and detail page."
    >
      <div className="flex items-start gap-4">
        <div className="flex h-20 w-32 shrink-0 items-center justify-center overflow-hidden rounded border border-sand bg-paper">
          {preview ? (
            <img src={preview} alt="" className="h-full w-full object-cover" />
          ) : (
            <span className="text-xs text-ink-soft">No photo</span>
          )}
        </div>

        <div className="min-w-0">
          <label className="inline-block cursor-pointer rounded border border-sand bg-white px-3 py-1.5 text-xs font-medium text-ink hover:bg-paper">
            {preview ? "Replace photo" : "Choose photo"}
            <input
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleChange}
            />
          </label>

          {file && (
            <button
              type="button"
              onClick={() => {
                setError("");
                onClear();
              }}
              className="ml-2 text-xs text-ink-soft underline hover:text-ink"
            >
              Undo
            </button>
          )}

          {file && (
            <p className="mt-2 truncate text-xs text-ink-soft">{file.name}</p>
          )}
          {error && <p className="mt-2 text-xs text-danger">{error}</p>}
        </div>
      </div>
    </Field>
  );
}

function TournamentForm({ tournament, onClose }) {
  const isEdit = Boolean(tournament);

  const [form, setForm] = useState(() => ({
    name: tournament?.name || "",
    date: apiDateToInput(tournament?.date) || "",
    location: tournament?.location || "",
    description: tournament?.description || "",
  }));

  const [photo, setPhoto] = useState(null);

  const { mutate: create, isLoading: isCreating } = useCreateTournament();
  const { mutate: update, isLoading: isUpdating } = useUpdateTournament();
  const { mutate: uploadPhoto, isLoading: isUploading } =
    useUploadTournamentPhoto();
  const isSaving = isCreating || isUpdating || isUploading;

  const set = (field) => (event) =>
    setForm((current) => ({ ...current, [field]: event.target.value }));

  const handleSubmit = (event) => {
    event.preventDefault();
    const payload = { ...form, date: inputDateToApi(form.date) };

    // The photo is a second request against the saved tournament's id, so it
    // can only run once the record exists. Closing is deferred until it lands,
    // otherwise the list would refresh before the image was attached.
    const finish = (saved) => {
      if (!photo) return onClose();
      uploadPhoto({ id: saved._id, file: photo }, { onSettled: onClose });
    };

    if (isEdit) {
      update({ id: tournament._id, ...payload }, { onSuccess: finish });
    } else {
      create(payload, { onSuccess: finish });
    }
  };

  return (
    <Modal
      title={isEdit ? `Edit ${tournament.name}` : "Add a tournament"}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            type="submit"
            form="tournament-form"
            variant="primary"
            disabled={isSaving}
          >
            {isSaving ? "Saving" : isEdit ? "Save changes" : "Add tournament"}
          </Button>
        </>
      }
    >
      <form
        id="tournament-form"
        onSubmit={handleSubmit}
        className="grid gap-5 sm:grid-cols-2"
      >
        <div className="sm:col-span-2">
          <Field label="Name">
            <Input value={form.name} onChange={set("name")} required autoFocus />
          </Field>
        </div>

        <Field label="Date">
          <Input
            type="date"
            value={form.date}
            onChange={set("date")}
            required
          />
        </Field>

        <Field label="Location">
          <Input value={form.location} onChange={set("location")} />
        </Field>

        <div className="sm:col-span-2">
          <Field label="Description">
            <Textarea
              rows={3}
              value={form.description}
              onChange={set("description")}
            />
          </Field>
        </div>

        <div className="sm:col-span-2">
          <PhotoField
            currentUrl={tournament?.imgUrl}
            file={photo}
            onPick={setPhoto}
            onClear={() => setPhoto(null)}
          />
        </div>
      </form>
    </Modal>
  );
}

export default function Tournaments() {
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const query = useTournaments();
  const { mutate: remove, isLoading: isDeleting } = useDeleteTournament();

  return (
    <>
      <PageHeader
        title="Tournaments"
        subtitle="The calendar. Categories, entries and draws arrive in the next phase."
        actions={
          <Button variant="primary" onClick={() => setEditing("new")}>
            Add tournament
          </Button>
        }
      />

      <Card>
        {query.isLoading ? (
          <Loader label="Loading tournaments" />
        ) : query.isError ? (
          <ErrorState
            message="Couldn't load tournaments."
            onRetry={query.refetch}
          />
        ) : (query.data || []).length === 0 ? (
          <EmptyState
            message="Nothing on the calendar yet."
            action={
              <Button variant="primary" onClick={() => setEditing("new")}>
                Add the first tournament
              </Button>
            }
          />
        ) : (
          <Table head={HEAD}>
            {query.data.map((tournament) => (
              <Row key={tournament._id}>
                <Cell>
                  <div className="flex h-10 w-16 items-center justify-center overflow-hidden rounded border border-sand bg-paper">
                    {tournament.imgUrl ? (
                      <img
                        src={tournament.imgUrl}
                        alt=""
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <span className="text-[10px] text-ink-soft">None</span>
                    )}
                  </div>
                </Cell>
                <Cell className="font-medium">{tournament.name}</Cell>
                <Cell className="font-mono text-xs tabular">
                  {formatDate(tournament.date)}
                </Cell>
                <Cell>
                  {tournament.location || (
                    <span className="text-ink-soft">Not set</span>
                  )}
                </Cell>
                <Cell align="right">
                  <div className="flex justify-end gap-2">
                    <Button
                      className="px-3 py-1.5 text-xs"
                      onClick={() => setEditing(tournament)}
                    >
                      Edit
                    </Button>
                    <Button
                      variant="danger"
                      className="px-3 py-1.5 text-xs"
                      onClick={() => setDeleting(tournament)}
                    >
                      Delete
                    </Button>
                  </div>
                </Cell>
              </Row>
            ))}
          </Table>
        )}
      </Card>

      {editing && (
        <TournamentForm
          tournament={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
        />
      )}

      {deleting && (
        <ConfirmDelete
          title={`Delete ${deleting.name}?`}
          body="The tournament is removed from the public calendar."
          isDeleting={isDeleting}
          onConfirm={() =>
            remove(deleting._id, { onSuccess: () => setDeleting(null) })
          }
          onClose={() => setDeleting(null)}
        />
      )}
    </>
  );
}
